import { axios } from 'services/axios';

/**
 * Normalise API response field names to match the form's internal names.
 * API → Form:
 *   pbx              → pbx
 *   addressID        → address (UUID, used to pre-select in dropdown)
 *   platformReference → siteId
 */
const normaliseSite = (site) => ({
    ...site,
    pbx: site.pbx ?? '',
    address: site.addressID ?? '',
    siteId: site.platformReference ?? '',
});

/**
 * Map form fields to the API POST payload.
 * Only the fields supported by the real API are included.
 */
const toCreatePayload = (fields, companyId, serviceId, platformId) => ({
    name: fields.name,
    description: fields.description ?? '',
    pbx: fields.pbx ?? '',
    addressID: fields.address || null,
    companyID: companyId,
    serviceID: serviceId,
    platformID: platformId,
});

/**
 * Map form fields to the API PUT payload.
 * Only name, description, and addressID are updatable.
 */
const toUpdatePayload = (id, fields) => ({
    id,
    name: fields.name,
    description: fields.description ?? '',
    addressID: fields.address || null,
});

export const getSites = async (serviceId, scope) => {
    const response = await axios.get(`/Sites/${serviceId}`, {
        headers: { 'X-RequestScope': scope },
    });
    const items = Array.isArray(response.data) ? response.data : [];
    return { status: 200, data: items.map(normaliseSite) };
};

export const getSiteById = async (id, scope) => {
    const response = await axios.get(`/Site/${id}`, {
        headers: { 'X-RequestScope': scope },
    });
    return { status: 200, data: normaliseSite(response.data) };
};

export const createSite = async (
    fields,
    companyId,
    serviceId,
    platformId,
    scope,
) => {
    const response = await axios.post(
        '/Site',
        toCreatePayload(fields, companyId, serviceId, platformId),
        { headers: { 'X-RequestScope': scope } },
    );
    return { status: 201, data: normaliseSite(response.data) };
};

export const updateSite = async (id, fields, scope) => {
    const response = await axios.put('/Site', toUpdatePayload(id, fields), {
        headers: { 'X-RequestScope': scope },
    });
    return { status: 200, data: normaliseSite(response.data) };
};

export const deleteSite = async (id, scope) => {
    const response = await axios.delete(`/Site/${id}`, {
        headers: { 'X-RequestScope': scope },
    });
    return { status: 200, data: response.data };
};

export const getAddresses = async (companyId, scope) => {
    const response = await axios.get(`/Addresses/${companyId}`, {
        headers: { 'X-RequestScope': scope },
    });
    const items = Array.isArray(response.data) ? response.data : [];
    return { status: 200, data: items };
};
